'use client';
import {AllMessagesRead, ChatEvent, ChatroomOpened} from '@web-libs/features/chat/application/use-chat.events';
import {ChatRepository} from '@web-libs/features/chat/domain/chat.repository';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
    addMessages,
    findChatroom,
    incrementUnreadMessages,
    isUnreadMessage, removeMessage,
    resetUnreadMessages,
} from '@web-libs/features/chat/domain/chat.functions';
import { useUnreadCounts } from '@web-libs/features/navigation/unread-counts.context';
import {sortChatroomsByLatestMessageDate} from '@web-libs/features/chat/domain/chatroom.functions';
import {useCurrentChatroom} from '@web-libs/features/chat/application/use-current-chatroom.concern';
import {useChatMessageHandler} from '@web-libs/features/chat/application/use-chat-message-handler';
import {ChatRoomChangeCallback, UseChatConcern} from '@web-libs/features/chat/application/use-chat.interface';
import {useChatroomLoading} from '@web-libs/features/chat/application/use-chatroom-loading.concern';
import {ChatMessage,} from '@web-libs/features/chat/domain/types/chat-message.types';
import {Chatroom, NewChatroom} from '@web-libs/features/chat/domain/types/chatroom.type';
import {Chat} from '@web-libs/features/chat/domain/types/chat.type';
import {useChatMessenger} from '@web-libs/features/chat/application/messenger/use-chat-messenger';
import {useInstitutionContext} from '@web-libs/features/institution/application/use-institution-context';

export type ChatEventHandler = (event: ChatEvent) => void;

export const useCreateChatConcern = (chatRepository: ChatRepository,
                                     chatEventHandler: ChatEventHandler): UseChatConcern => {
    const [chat, setChat] = useState<Chat>({chatrooms: []});
    const [chatLoading, setChatLoading] = useState<boolean>(true);
    const { updateUnreadCounts, unreadCounts } = useUnreadCounts();
    const {
        currentOpenChatroom,
        currentChatroomError,
        openChatroom,
        tryOpeningFirstChatroom,
        currentChatroomLoading: _currentChatroomLoading,
        setCurrentChatroomLoading
    } = useCurrentChatroom(
        setChat,
        chatRepository,
        (id) => {
            chatEventHandler(new ChatroomOpened(id));
        }
    );

    const [chatActive, _setChatActive] = useState<boolean>(true);

    const {isLoading, setLoading} = useChatroomLoading();

    const currentChatroomLoadingMore = currentOpenChatroom ? isLoading(currentOpenChatroom) : false;
    const currentChatroomLoading = _currentChatroomLoading;

    useEffect(() => {
                  if (currentOpenChatroom) {
                      setLoading(currentOpenChatroom,
                                 _currentChatroomLoading);
                  }
              },
              [_currentChatroomLoading, currentOpenChatroom]);

    const {registerMessageHandleOnce} = useChatMessageHandler(
        chatRepository,
        ({message, chatroomId}) => {
            if ((!chatActive || currentOpenChatroom !== chatroomId) && !message.ownMessage) {
                setChat(chat => incrementUnreadMessages(chat,
                                                        chatroomId));
                // Update the global unread counts context
                updateUnreadCounts({ countChatMessages: unreadCounts.countChatMessages + 1 });
            }
            addChatroomMessages(chatroomId,
                                [message]);
        },
        [currentOpenChatroom, chat, unreadCounts]
    );

    useEffect(() => {
                  setChatLoading(true);
                  chatRepository.getChat().then((chat) => {
                      setChat(chat);
                      if (chat.chatrooms.length === 0) {
                          setCurrentChatroomLoading(false);
                      }

                      // Calculate total unread messages across all chatrooms
                      const totalUnreadMessages = chat.chatrooms.reduce(
                          (total, chatroom) => total + (chatroom.unreadMessages || 0),
                          0
                      );

                      // Update the global unread counts context
                      updateUnreadCounts({ countChatMessages: totalUnreadMessages });
                  }).finally(() => {
                      setChatLoading(false);
                  });
              },
              [updateUnreadCounts]);


    const chatrooms = useMemo(() => {
                                  return sortChatroomsByLatestMessageDate(chat.chatrooms);
                              },
                              [chat]);

    useEffect(() => {
                  tryOpeningFirstChatroom(chatrooms);
                  if (chat) {
                      registerMessageHandleOnce();
                  }
              },
              [chat]);

    const isUnreadMessageAvailable = isUnreadMessage(chat);

    const allMessagesRead = (chatroomId: string) => {
        chatEventHandler(new AllMessagesRead(chatroomId));
    };

    const addChatroomMessages = (chatroomId: string,
                                 messages: ChatMessage[]) => {
        setChat(chat => addMessages(chat,
                                    chatroomId,
                                    messages));
    };
    const getChatroom = (chatroomId: string) => findChatroom(chat,
                                                             chatroomId);

    const createChatroom = async (newChatroom: NewChatroom) => {
        const newCreatedChatroom = await chatRepository.createChatroom(newChatroom);
        setChat(chat => {
            if (chat.chatrooms.some(c => c.id === newCreatedChatroom.id)) {
                return chat;
            }
            return ({
                ...chat,
                chatrooms: [...chat.chatrooms, newCreatedChatroom]
            });
        });
        openChatroom(newCreatedChatroom.id);
        return;
    };

    const instId = useInstitutionContext();

    const startInstitutionChat = async () => {
        const newCreatedChatroom = await chatRepository.startInstitutionChat(instId);
        setChat(chat => ({
            ...chat,
            chatrooms: [...chat.chatrooms, newCreatedChatroom]
        }));
        openChatroom(newCreatedChatroom.id);
        return;
    };

    const chatroomChangeListener = useRef<ChatRoomChangeCallback[]>([]);
    const onChatroomsChange = (callback: ChatRoomChangeCallback) => {
        chatroomChangeListener.current.push(callback);
    };

    const removeChatroomChangeListener = (callback: ChatRoomChangeCallback) => {
        chatroomChangeListener.current = chatroomChangeListener.current.filter(cb => cb !== callback);
    };

    const previousChatrooms = useRef<Chatroom[]>(chatrooms);

    useEffect(() => {
                  if (previousChatrooms.current.length !== chatrooms.length) {
                      chatroomChangeListener.current.forEach(callback => callback(chatrooms));
                  }
                  previousChatrooms.current = chatrooms;
              },
              [chatrooms]);

    const upsertChatMessage = (chatroomId: string,
                               message: ChatMessage) => {
        setChat(chat => addMessages(chat,
                                    chatroomId,
                                    [message]));
    };
    const {
        sendTextMessage,
        sendFileMessage,
        sendMediaMessage,
        sendVoiceMessage,
        resendMessage
    } = useChatMessenger(setChat,
                         upsertChatMessage,
                         chatRepository,
                         chatrooms);

    const [connectionState, setConnectionState] = useState<boolean>(true);
    const connectionStateConcern = {
        isBadConnection: !connectionState,
        setIsConnected: setConnectionState
    };

    const _resetUnreadMessages = (chatroomId: string) => {
        setChat(chat => {
            const updatedChat = resetUnreadMessages(chat, chatroomId);

            // Calculate total unread messages across all chatrooms
            const totalUnreadMessages = updatedChat.chatrooms.reduce(
                (total, chatroom) => total + (chatroom.unreadMessages || 0),
                0
            );

            // Update the global unread counts context
            updateUnreadCounts({ countChatMessages: totalUnreadMessages });

            return updatedChat;
        });
    };

    useEffect(() => {
                  if (chatActive && currentOpenChatroom) {
                      _resetUnreadMessages(currentOpenChatroom);
                  }
              },
              [chatActive]);

    const deleteChatMessage = (chatroomId: string,
                                 messageId: string) => {
          chatRepository.deleteMessage(chatroomId,
                                         messageId);

            setChat(chat =>
                removeMessage(chat,
                                chatroomId,
                                messageId)
            )
     };


    return {
        chatLoading,
        chatrooms,
        getChatroom,
        connectionState: connectionStateConcern,
        isUnreadMessageAvailable,
        sendMessage: sendTextMessage,
        sendFileMessage,
        sendMediaMessage,
        sendVoiceMessage,
        resendMessage,
        openChatroom,
        allMessagesRead,
        addChatroomMessages,
        currentOpenChatroom,
        currentChatroomError,
        currentChatroomLoading,
        currentChatroomLoadingMore,
        isChatroomLoading: isLoading,
        setChatroomLoading: setLoading,
        createChatroom,
        startInstitutionChat,
        onChatroomsChange,
        removeChatroomChangeListener,
        chatActive,
        setChatActive: _setChatActive,
        deleteChatMessage
    };
};
